import { Component } from '@theme/component';

/**
 * PDF Viewer component that renders PDF pages as slideshow slides
 * Supports fullscreen and zoom functionality
 *
 * @extends {Component}
 */
export class PDFViewer extends Component {
  // PDF.js library
  #pdfjsLib = null;
  #pdfDoc = null;
  #currentZoom = 1;
  #isFullscreen = false;

  async connectedCallback() {
    super.connectedCallback();

    console.log('PDF Viewer connected');

    // Wait a moment for DOM to be ready
    await new Promise(resolve => setTimeout(resolve, 100));

    // Load PDF.js library from CDN
    try {
      await this.#loadPDFJS();
      console.log('PDF.js loaded successfully');
    } catch (error) {
      console.error('Failed to load PDF.js:', error);
      this.setAttribute('error', '');
      return;
    }

    // Get PDF URL from attribute
    const pdfUrl = this.getAttribute('pdf-url');
    if (!pdfUrl) {
      console.error('PDF URL not provided');
      this.setAttribute('error', '');
      return;
    }

    console.log('PDF URL:', pdfUrl);

    // Load and render PDF
    await this.#loadPDF(pdfUrl);

    // Setup controls
    this.#setupControls();
  }

  /**
   * Load PDF.js library from CDN
   */
  async #loadPDFJS() {
    return new Promise((resolve, reject) => {
      console.log('Checking if PDF.js is already loaded...');

      if (window.pdfjsLib) {
        console.log('PDF.js already available in window');
        this.#pdfjsLib = window.pdfjsLib;
        resolve();
        return;
      }

      console.log('Loading PDF.js from CDN...');

      // Load PDF.js from CDN
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.async = false; // Load synchronously to ensure it's ready
      script.type = 'text/javascript';

      script.onload = () => {
        console.log('PDF.js script loaded');

        // Wait for pdfjsLib to be available
        const checkLib = () => {
          if (window.pdfjsLib) {
            console.log('PDF.js library is now available');
            this.#pdfjsLib = window.pdfjsLib;
            // Set worker
            this.#pdfjsLib.GlobalWorkerOptions.workerSrc =
              'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            console.log('Worker source set');
            resolve();
          } else {
            console.log('Waiting for pdfjsLib...');
            setTimeout(checkLib, 100);
          }
        };
        checkLib();
      };

      script.onerror = (error) => {
        console.error('Failed to load PDF.js script:', error);
        reject(new Error('Failed to load PDF.js'));
      };

      console.log('Appending script to head...');
      document.head.appendChild(script);
    });
  }

  /**
   * Load PDF document
   */
  async #loadPDF(url) {
    try {
      // Show loading state
      this.setAttribute('loading', '');

      console.log('Loading PDF from:', url);

      const loadingTask = this.#pdfjsLib.getDocument(url);
      this.#pdfDoc = await loadingTask.promise;

      const numPages = this.#pdfDoc.numPages;
      console.log('PDF loaded, total pages:', numPages);

      // Update page count display
      const totalPages = this.querySelector('[ref="totalPages"]');
      if (totalPages) {
        totalPages.textContent = numPages;
      }

      // Render all pages
      await this.#renderAllPages();

      // Remove loading state
      this.removeAttribute('loading');

      console.log('PDF rendering complete');

      // Initialize slideshow after rendering
      this.#initializeSlideshow();

    } catch (error) {
      console.error('Error loading PDF:', error);
      this.setAttribute('error', '');
      this.removeAttribute('loading');
    }
  }

  /**
   * Initialize slideshow after PDF pages are rendered
   */
  #initializeSlideshow() {
    const slideshow = this.querySelector('slideshow-component');
    if (slideshow) {
      console.log('Slideshow found, force re-initializing...');

      // Force slideshow to re-initialize by disconnecting and reconnecting
      const parent = slideshow.parentElement;
      const nextSibling = slideshow.nextSibling;

      slideshow.remove();

      // Wait a tick for cleanup
      requestAnimationFrame(() => {
        if (nextSibling && parent) {
          parent.insertBefore(slideshow, nextSibling);
        } else if (parent) {
          parent.appendChild(slideshow);
        }

        console.log('Slideshow re-connected');

        // Setup slideshow event listener after re-connection
        setTimeout(() => {
          this.#setupSlideshowEvents();
        }, 100);
      });
    }
  }

  /**
   * Setup slideshow event listeners
   */
  #setupSlideshowEvents() {
    const slideshow = this.querySelector('slideshow-component');
    if (slideshow) {
      console.log('Setting up slideshow event listeners...');

      // Remove old listener if exists
      slideshow.removeEventListener('slideshow:select', this.#handleSlideChange);

      // Add new listener
      slideshow.addEventListener('slideshow:select', this.#handleSlideChange);

      console.log('Event listeners attached');
    }
  }

  /**
   * Handle slide change event
   */
  #handleSlideChange = (event) => {
    console.log('Slide changed:', event.detail);
    this.#updateCurrentPage(event.detail.index + 1);
  };

  /**
   * Render all PDF pages into the slideshow
   */
  async #renderAllPages() {
    const numPages = this.#pdfDoc.numPages;

    // Find slides container using querySelector
    const slidesContainer = this.querySelector('slideshow-slides');

    if (!slidesContainer) {
      console.error('Slides container not found');
      console.log('Looking for slideshow-slides element...');
      console.log('PDF Viewer HTML:', this.innerHTML.substring(0, 500));
      return;
    }

    console.log('Slides container found:', slidesContainer);

    // Get quality setting (1-3, where 3 is highest)
    const quality = parseInt(this.getAttribute('quality') || '2', 10);
    const scale = quality; // 1x, 2x, or 3x

    console.log('Rendering quality:', quality);

    // Render pages sequentially to avoid memory issues
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      await this.#renderPage(pageNum, scale);
      console.log(`Rendered page ${pageNum}/${numPages}`);
    }
  }

  /**
   * Render a single PDF page
   */
  async #renderPage(pageNum, scale = 2) {
    try {
      const page = await this.#pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });

      // Create canvas for this page
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.className = 'pdf-page-canvas';

      // Render PDF page into canvas
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;

      // Create slide element
      const slide = this.#createSlide(pageNum, canvas);

      // Add to slideshow
      const slidesContainer = this.querySelector('slideshow-slides');
      if (slidesContainer) {
        slidesContainer.appendChild(slide);
      }
    } catch (error) {
      console.error(`Error rendering page ${pageNum}:`, error);
    }
  }

  /**
   * Create a slideshow slide element with the rendered canvas
   */
  #createSlide(pageNum, canvas) {
    const slide = document.createElement('slideshow-slide');
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('aria-hidden', pageNum === 1 ? 'false' : 'true');
    slide.setAttribute('slide-id', `pdf-page-${pageNum}`);
    slide.style.setProperty('view-timeline-name', `--slide-${pageNum - 1}`);

    // Create wrapper for zoom functionality
    const wrapper = document.createElement('div');
    wrapper.className = 'pdf-page-wrapper';
    wrapper.style.transform = `scale(${this.#currentZoom})`;
    wrapper.appendChild(canvas);

    slide.appendChild(wrapper);

    return slide;
  }

  /**
   * Setup control buttons (fullscreen, zoom)
   */
  #setupControls() {
    // Fullscreen toggle
    const fullscreenBtn = this.querySelector('[data-action="fullscreen"]');
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', () => this.toggleFullscreen());
    }

    // Zoom in
    const zoomInBtn = this.querySelector('[data-action="zoom-in"]');
    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', () => this.zoomIn());
    }

    // Zoom out
    const zoomOutBtn = this.querySelector('[data-action="zoom-out"]');
    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', () => this.zoomOut());
    }

    // Zoom reset
    const zoomResetBtn = this.querySelector('[data-action="zoom-reset"]');
    if (zoomResetBtn) {
      zoomResetBtn.addEventListener('click', () => this.resetZoom());
    }

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', () => {
      this.#isFullscreen = !!document.fullscreenElement;
      if (this.#isFullscreen) {
        this.setAttribute('fullscreen', '');
      } else {
        this.removeAttribute('fullscreen');
      }
    });

    // Note: Slideshow event listeners are setup in #setupSlideshowEvents()
    // after slideshow is re-initialized
  }

  /**
   * Update current page display
   */
  #updateCurrentPage(pageNum) {
    const currentPageEl = this.querySelector('[ref="currentPage"]');
    if (currentPageEl) {
      currentPageEl.textContent = pageNum;
    }
  }

  /**
   * Toggle fullscreen mode
   */
  async toggleFullscreen() {
    if (!document.fullscreenElement) {
      try {
        await this.requestFullscreen();
        this.#isFullscreen = true;
        this.setAttribute('fullscreen', '');
      } catch (error) {
        console.error('Error entering fullscreen:', error);
      }
    } else {
      try {
        await document.exitFullscreen();
        this.#isFullscreen = false;
        this.removeAttribute('fullscreen');
      } catch (error) {
        console.error('Error exiting fullscreen:', error);
      }
    }
  }

  /**
   * Zoom in
   */
  zoomIn() {
    this.#currentZoom = Math.min(this.#currentZoom + 0.25, 3);
    this.#applyZoom();
  }

  /**
   * Zoom out
   */
  zoomOut() {
    this.#currentZoom = Math.max(this.#currentZoom - 0.25, 0.5);
    this.#applyZoom();
  }

  /**
   * Reset zoom to 1x
   */
  resetZoom() {
    this.#currentZoom = 1;
    this.#applyZoom();
  }

  /**
   * Apply current zoom level to all pages
   */
  #applyZoom() {
    const wrappers = this.querySelectorAll('.pdf-page-wrapper');
    wrappers.forEach(wrapper => {
      wrapper.style.transform = `scale(${this.#currentZoom})`;
    });

    // Update zoom display
    const zoomDisplay = this.querySelector('[ref="zoomLevel"]');
    if (zoomDisplay) {
      zoomDisplay.textContent = `${Math.round(this.#currentZoom * 100)}%`;
    }
  }

  /**
   * Get current page number from slideshow
   */
  get currentPage() {
    const slideshow = this.querySelector('slideshow-component');
    if (!slideshow || !slideshow.current) return 1;
    return (slideshow.current || 0) + 1;
  }
}

// Register custom element
if (!customElements.get('pdf-viewer')) {
  customElements.define('pdf-viewer', PDFViewer);
}
